import React, { useState } from "react";
import {
  STOCK_ADVISOR_ACTIONS, SHELF_SPACE_RECS, STAFFING_INSIGHTS, REBALANCE_MANIFEST,
} from "../lib/dataUseCases";
import { Tile, Badge, SectionHeader, StatusDot } from "../components/Primitives";
import { formatPeso, formatNumber, pct, cn } from "../lib/utils";
import { useCopilot } from "../lib/copilot.jsx";
import {
  ArrowRight, ArrowRightLeft, CheckCircle2, Clock, AlertTriangle, RefreshCw, MessageSquare,
  Sparkles, Trash2, Plus, FileEdit, Users,
} from "lucide-react";

const ACTION_ICON = {
  "Replenish": RefreshCw,
  "Add to PO": Plus,
  "Transfer In": ArrowRightLeft,
  "Overstock Review": Trash2,
  "Expedite": Clock,
  "Review Forecast": FileEdit,
  "Branch Action": Users,
  "New SKU": Sparkles,
};

export default function Operations() {
  const { askCopilot } = useCopilot();
  const [actionStatus, setActionStatus] = useState({});

  const totalImpact = STOCK_ADVISOR_ACTIONS.reduce((s, a) => s + a.impact, 0);
  const criticalCount = STOCK_ADVISOR_ACTIONS.filter(a => a.urgency === "critical").length;

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="PART V + VII · Operations" title="Daily Operations Command"
        description="AI Stock Advisor 'what should I do today?', branch-level network rebalancing, shelf-space & staffing optimization." />

      <div className="tile grid grid-cols-2 md:grid-cols-5 divide-x divide-border">
        <Agg label="Daily Actions" value={STOCK_ADVISOR_ACTIONS.length} status="info" />
        <Agg label="Critical" value={criticalCount} status="critical" />
        <Agg label="₱ Impact at Stake" value={formatPeso(totalImpact, { compact: true })} status="warning" />
        <Agg label="Rebalance Manifest" value={REBALANCE_MANIFEST.length} status="info" />
        <Agg label="Shelf Recs" value={SHELF_SPACE_RECS.length} status="info" />
      </div>

      <div className="grid grid-cols-12 gap-6">

        {/* AI Stock Advisor */}
        <Tile className="col-span-12" testid="stock-advisor"
              title='AI Stock Advisor — "What Should I Do Today?"'
              action={<div className="flex items-center gap-2">
                <Badge status="critical">{criticalCount} critical</Badge>
                <button
                  data-testid="ask-copilot-stock-advisor"
                  onClick={() => askCopilot(
                    `As COO, I'm reviewing the AI Stock Advisor's daily action queue. There are ${STOCK_ADVISOR_ACTIONS.length} recommended actions today across the network: ${criticalCount} critical, total ₱ impact at stake ${formatPeso(totalImpact, { compact: true })}. The top critical items are: ${STOCK_ADVISOR_ACTIONS.filter(a => a.urgency === "critical").map(a => `(${a.role}) "${a.title}" — ₱${(a.impact/1e6).toFixed(1)}M impact, ${(a.confidence*100).toFixed(0)}% confidence, reason: ${a.reason}`).join("; ")}. Give me a sequenced action plan: which 3 should I escalate to my exec team first, what dependencies do I need to clear (cash, supplier capacity, transport), and what KPIs should I expect to move in the next 2 weeks if I execute all of them?`
                  )}
                  className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-medium"
                >
                  <MessageSquare size={11} /> Ask Copilot →
                </button>
              </div>}>
          <div className="max-h-[500px] overflow-y-auto -mx-4">
            <div className="space-y-0 divide-y divide-border">
              {STOCK_ADVISOR_ACTIONS.map(a => {
                const Icon = ACTION_ICON[a.action] || Sparkles;
                const status = actionStatus[a.id];
                return (
                  <div key={a.id} data-testid={`stock-action-${a.id}`}
                       className={cn("px-4 py-3 flex items-start gap-3",
                         status === "done" ? "bg-success/5" : status === "deferred" ? "bg-surface-2/50 opacity-60" : "")}>
                    <div className={cn("w-9 h-9 rounded-sm flex items-center justify-center shrink-0 mt-0.5",
                      a.urgency === "critical" ? "bg-critical/15 text-critical" : a.urgency === "warning" ? "bg-warning/15 text-warning" : "bg-info/15 text-info")}>
                      <Icon size={15} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge status="neutral">{a.action}</Badge>
                        <span className="text-[10px] text-muted">{a.role}</span>
                      </div>
                      <div className="text-sm font-medium mt-1">{a.title}</div>
                      <div className="text-[11px] text-muted mt-0.5">{a.reason}</div>
                    </div>
                    <div className="text-right shrink-0 space-y-1">
                      <div className="font-mono-tabular text-sm font-semibold text-primary">{formatPeso(a.impact, { compact: true })}</div>
                      <div className="text-[10px] text-muted font-mono-tabular">{(a.confidence * 100).toFixed(0)}% conf</div>
                      {!status ? (
                        <div className="inline-flex gap-1">
                          <button data-testid={`action-execute-${a.id}`}
                                  onClick={() => setActionStatus(s => ({ ...s, [a.id]: "done" }))}
                                  className="px-2 py-0.5 text-[10px] border border-success/40 text-success hover:bg-success/10 rounded-sm">Execute</button>
                          <button data-testid={`action-defer-${a.id}`}
                                  onClick={() => setActionStatus(s => ({ ...s, [a.id]: "deferred" }))}
                                  className="px-2 py-0.5 text-[10px] border border-border text-muted hover:bg-surface-2 rounded-sm">Defer</button>
                        </div>
                      ) : (
                        <Badge status={status === "done" ? "success" : "neutral"}>{status === "done" ? "✓ Executed" : "Deferred"}</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Tile>

        {/* Branch Network Rebalancing */}
        <Tile className="col-span-12 lg:col-span-7" testid="rebalance-manifest"
              title="Branch Network Rebalancing Manifest"
              action={<Badge status="info">{formatPeso(REBALANCE_MANIFEST.reduce((s, t) => s + t.valueMoved, 0), { compact: true })} moves · {formatPeso(REBALANCE_MANIFEST.reduce((s, t) => s + t.costTransport, 0))} transport</Badge>}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                <th className="py-2 px-3 text-left">SKU</th>
                <th className="py-2 px-3 text-right">Units</th>
                <th className="py-2 px-3 text-left">From → To</th>
                <th className="py-2 px-3 text-right">Value</th>
                <th className="py-2 px-3 text-right">Transport ₱</th>
                <th className="py-2 px-3 text-right">Health Δ</th>
              </tr>
            </thead>
            <tbody className="font-mono-tabular">
              {REBALANCE_MANIFEST.map(t => (
                <tr key={t.id} className="border-b border-border hover:bg-surface-2" data-testid={`rebalance-${t.id}`}>
                  <td className="py-2 px-3 font-sans"><div className="font-medium text-sm">{t.sku}</div></td>
                  <td className="py-2 px-3 text-right">{t.units}</td>
                  <td className="py-2 px-3 font-sans">
                    <div className="inline-flex items-center gap-1.5 text-[11px]">
                      <span className="text-muted">{t.from}</span>
                      <ArrowRight size={11} className="text-primary" />
                      <span className="text-fg font-medium">{t.to}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right">{formatPeso(t.valueMoved, { compact: true })}</td>
                  <td className="py-2 px-3 text-right text-muted">{formatPeso(t.costTransport)}</td>
                  <td className="py-2 px-3 text-right text-success font-semibold">+{(t.healthGain * 100).toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tile>

        {/* Shelf Space recommendations */}
        <Tile className="col-span-12 lg:col-span-5" testid="shelf-space"
              title="Shelf-Space Optimization"
              action={<Badge status="success">Avg +6.1% uplift</Badge>}>
          <div className="space-y-3">
            {SHELF_SPACE_RECS.map(r => (
              <div key={r.branchId} data-testid={`shelf-${r.branchId}`} className="p-3 border border-border rounded-sm bg-surface-2/40">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-medium text-sm">{r.name}</div>
                  <Badge status="success">+{(r.upliftPct * 100).toFixed(1)}%</Badge>
                </div>
                <div className="text-[10px] text-muted mb-2 font-mono-tabular"><strong>Now:</strong> {r.currentMix}</div>
                <div className="text-[10px] text-primary mb-2 font-mono-tabular"><strong>Recommended:</strong> {r.recommendedMix}</div>
                <div className="text-[11px] text-fg/80">{r.reason}</div>
              </div>
            ))}
          </div>
        </Tile>

        {/* Staffing Efficiency */}
        <Tile className="col-span-12" testid="staffing"
              title="Branch Staffing Efficiency — Transactions per Staff-Hour"
              action={<div className="text-[10px] uppercase tracking-widest text-muted">Network benchmark: 7.2 txn/staff-hr</div>}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                <th className="py-2 px-3 text-left">Branch</th>
                <th className="py-2 px-3 text-right">Txn / Staff-Hr</th>
                <th className="py-2 px-3 text-right">vs Benchmark</th>
                <th className="py-2 px-3 text-left">Status</th>
                <th className="py-2 px-3 text-left">Recommendation</th>
              </tr>
            </thead>
            <tbody className="font-mono-tabular">
              {STAFFING_INSIGHTS.map(s => (
                <tr key={s.branchId} className="border-b border-border" data-testid={`staff-${s.branchId}`}>
                  <td className="py-2 px-3 font-sans font-medium">{s.name}</td>
                  <td className="py-2 px-3 text-right font-semibold">{s.txnPerStaffHour}</td>
                  <td className={cn("py-2 px-3 text-right", s.txnPerStaffHour > s.benchmark ? "text-success" : "text-warning")}>
                    {s.txnPerStaffHour > s.benchmark ? "+" : ""}{(s.txnPerStaffHour - s.benchmark).toFixed(1)}
                  </td>
                  <td className="py-2 px-3 font-sans"><Badge status={s.status === "above" ? "success" : "warning"}>{s.status}</Badge></td>
                  <td className="py-2 px-3 font-sans text-[11px] text-muted">{s.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tile>
      </div>
    </div>
  );
}

function Agg({ label, value, status }) {
  return (
    <div className="px-4 py-3">
      <div className="kpi-label flex items-center gap-2">{status && <StatusDot status={status} />}{label}</div>
      <div className="kpi-value text-2xl mt-1.5">{value}</div>
    </div>
  );
}
